import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { throttle } from "lodash";
import { BREAKPOINT_DESKTOP } from "../../constants/Breakpoints";
import MainMenu from "../MainMenu/MainMenu";
import LogoHeader from "../LogoHeader/LogoHeader";
import SearchBlock from "../SearchBlock/SearchBlock";
import ButtonClose from "../ButtonClose/ButtonClose";
import ButtonHeader from "../ButtonHeader/ButtonHeader";
import HeaderModal from "../HeaderModal/HeaderModal";
import { ReactComponent as SearchSvg } from "../../images/search.svg";
import useMedia from "../../lib/hooks/useMedia";
import languageDir from "../../constants/languageDir";

const Header = ({
  navigationBlock,
  themeHeader,
  getStockData,
  languageDirection,
  typeOfSearch,
  linkToSite,
  typeOfMenu,
  showStockData,
}) => {
  const [isMobilePopUp, setIsMobilePopUp] = useState(false);
  const [contentModal, setContentModal] = useState("");
  const [changeTheme, setChangeTheme] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [focusInput, setFocusInput] = useState(false);
  const buttonFocus = useRef(null);
  const menuBlock = "menu";
  const searchBlockText = "search";
  const searchBlockPlaceholder = "search l3harris";
  const popupOpenClass = "popup-header--open";
  const isMobile = useMedia(`(max-width: ${BREAKPOINT_DESKTOP - 1}px)`);

  // Func for show/hide block with menu/search on mobile.
  const togglePopup = (e) => {
    const getAttrButton = e.currentTarget.getAttribute("data-modal");
    setContentModal(getAttrButton);
    setIsMobilePopUp(!isMobilePopUp);
    if (getAttrButton === searchBlockText) {
      setFocusInput(true);
    } else {
      setFocusInput(false);
    }
    document.body.classList.toggle(popupOpenClass);
  };

  useEffect(() => {
    if (contentModal === menuBlock) {
      buttonFocus.current.focus();
    }
  }, [contentModal]);

  // Throttled callback for scroll function.
  // For change theme of header.
  const offsetHeader = 20;
  const scrollHandler = useCallback(() => {
    const positionOfPage = window.pageYOffset;
    // Add/remove class for header on scroll.
    if (!changeTheme && positionOfPage > offsetHeader) {
      setChangeTheme(true);
    } else if (changeTheme && positionOfPage <= offsetHeader) {
      setChangeTheme(false);
    }
  }, [changeTheme]);

  const throttledScroll = throttle(scrollHandler, 300, { leading: false });

  useEffect(() => {
    // If white theme not need scroll.
    if (themeHeader === "white" || showDesktopSearch)
      return window.removeEventListener("scroll", throttledScroll);
    window.addEventListener("scroll", throttledScroll);
    return () => {
      window.removeEventListener("scroll", throttledScroll);
    };
  }, [showDesktopSearch, themeHeader, throttledScroll]);

  // If resize window hide popup for desktop.
  useEffect(() => {
    if (!isMobile) {
      document.body.classList.remove(popupOpenClass);
      setIsMobilePopUp(false);
    }
  }, [isMobile]);

  // Get stock date and send data to main menu.
  const [stockData, setStockData] = useState({});
  useEffect(() => {
    if (showStockData) {
      getStockData().then((data) => {
        return Object.keys(data).length !== 0 ? setStockData(data) : null;
      });
    }
  }, [getStockData, showStockData, stockData]);

  const themeHeaderClass =
    window.pageYOffset > offsetHeader || changeTheme ? "white" : themeHeader;

  const mainSiteLink = linkToSite ? (
    <a
      href="/"
      className="link link--tertiary link--small link--back-to-site color-transform"
    >
      {linkToSite}
    </a>
  ) : null;

  return (
    <div
      className={`header-wrapper pt-12 pb-4 pb-lg-0 pt-lg-24 header-wrapper--${themeHeaderClass}`}
      dir={languageDirection}
    >
      <div className="container d-flex justify-content-between">
        <LogoHeader />
        {isMobile && (
          <div className="d-print-none">
            <ButtonHeader
              className={`popup__button popup__button--open color-transform mt-8 mt-md-4 d-lg-none ${
                languageDirection === languageDir.rtl ? "ml-24" : "mr-24"
              }`}
              handleClick={(e) => togglePopup(e)}
              dataModal={searchBlockText}
              ariaLabel="Open search"
              buttonContent={<SearchSvg />}
            />
            <ButtonHeader
              className="popup__button popup__button--open color-transform mt-4 d-lg-none"
              handleClick={(e) => togglePopup(e)}
              dataModal={menuBlock}
              ariaLabel="Open menu popup"
              buttonContent={
                <>
                  <i className="mb-4 d-block" />
                  <i className="mb-4 d-block" />
                  <i className="d-block" />
                </>
              }
            />
          </div>
        )}
        {isMobile && (
          <div className={isMobilePopUp ? "d-block" : "d-none"}>
            <HeaderModal
              modalContent={
                <>
                  <div className="">
                    <LogoHeader isMobilePopUp={isMobilePopUp} />
                  </div>
                  <ButtonClose
                    toggleModal={togglePopup}
                    focusButton={buttonFocus}
                  />
                  <div
                    className={
                      contentModal === menuBlock ? "d-block" : "d-none"
                    }
                  >
                    <MainMenu
                      navigationBlock={navigationBlock}
                      isMobile={isMobile}
                      buttonFocus={buttonFocus}
                      stockData={stockData}
                      mainSiteLink={mainSiteLink}
                      languageDirection={languageDirection}
                      toggleModal={togglePopup}
                    />
                  </div>
                  <div
                    className={` mt-52 px-24 ${
                      contentModal === searchBlockText ? "d-block" : "d-none"
                    }`}
                  >
                    <SearchBlock
                      focusInputSearch={focusInput}
                      placeholderText={searchBlockPlaceholder}
                    />
                  </div>
                </>
              }
            />
          </div>
        )}
        {!isMobile && (
          <div className="position-relative d-none d-lg-flex">
            <div
              aria-hidden={showDesktopSearch}
              className={showDesktopSearch ? "d-none" : ""}
            >
              <MainMenu
                navigationBlock={navigationBlock}
                isMobile={isMobile}
                buttonFocus={buttonFocus}
                stockData={stockData}
                setChangeTheme={setChangeTheme}
                setShowDesktopSearch={setShowDesktopSearch}
                languageDirection={languageDirection}
                mainSiteLink={mainSiteLink}
                typeOfMenu={typeOfMenu}
              />
            </div>
            <div
              className={`header__search ${
                showDesktopSearch ? "d-block" : "d-none"
              }`}
            >
              <SearchBlock
                focusInputSearch={showDesktopSearch}
                placeholderText={searchBlockPlaceholder}
                typeOfSearch={typeOfSearch}
              />
              <button
                className="popup__button popup__button--close"
                onClick={() => {
                  setShowDesktopSearch(false);
                }}
                type="button"
                aria-label="Close search"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;

Header.defaultProps = {
  navigationBlock: {},
  getStockData: () => {},
  typeOfSearch: "",
  linkToSite: "",
  showStockData: true,
};

Header.propTypes = {
  navigationBlock: PropTypes.object,
  themeHeader: PropTypes.string.isRequired,
  getStockData: PropTypes.func,
  languageDirection: PropTypes.string.isRequired,
  typeOfSearch: PropTypes.string,
  linkToSite: PropTypes.string,
  typeOfMenu: PropTypes.string.isRequired,
  showStockData: PropTypes.bool,
};
